import { useEffect, useState } from 'react'
import MuiAccordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import { Box, useTheme } from '@mui/material'
import CircleIcon from './CircleIcon'

interface Props {
  children: React.ReactNode
  title: string
  subTitle?: string
  titleEndComponent?: React.ReactNode
  disabled?: boolean
  onExpand?: () => void
  open?: boolean
  variant?: 'default' | 'borderless'
  closeOnClick?: boolean
  labelGridWidth?: number | 'auto'
}

export default function Accordion(props: Props) {
  const theme = useTheme()
  const {
    children,
    title,
    subTitle,
    titleEndComponent,
    disabled,
    onExpand,
    open,
    variant = 'default',
    closeOnClick = true,
    labelGridWidth = 24,
  } = props

  const labelWidth = theme.spacing(labelGridWidth)
  const [expanded, setExpanded] = useState(open)

  useEffect(() => {
    setExpanded(open)
  }, [open])

  return (
    <Box>
      <MuiAccordion
        sx={{
          border: variant === 'default' ? '1px solid' : 'none',
          borderColor: variant === 'default' ? 'background.default' : 'transparent',
          '&.MuiPaper-root': {
            boxSizing: 'border-box',
          },
          '&:hover.MuiPaper-root ': {
            '@media (hover: hover)': { // This only applies the hover styles if :hover is supported, e.g. not on touch devices
              backgroundColor: variant === 'default' ? theme.palette.secondary.light : 'transparent',
            },
          },
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
        expanded={expanded}
        elevation={0}
        onChange={() => {
          if (expanded && !closeOnClick) {
            return
          }
          setExpanded(!expanded)
          onExpand?.()
        }}
      >
        <AccordionSummary
          sx={{
            margin: 0,
            flexDirection: 'row-reverse',
            minHeight: theme.spacing(7),
            px: 3,
            py: 3,
            '&.Mui-expanded': {
              minHeight: theme.spacing(7),
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0,
            },
            '& .MuiSvgIcon-root': {
              transition: 'transform 0.3s ease',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(-90deg)',
            },
            '& :active': {
              transition: 'none',
              backgroundColor: variant === 'default' ? theme.palette.secondary.light : 'transparent',
            },
            '@media (hover: hover)': { // This only applies the hover styles if :hover is supported, e.g. not on touch devices
              '&:hover.MuiAccordionSummary-root': {
                backgroundColor: variant === 'borderless' ? theme.palette.secondary.light : 'transparent',
              },
            },
          }}
          expandIcon={<CircleIcon color='secondary' icon8='smExpandArrow' size='nano' />}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              marginLeft: theme.spacing(2),
              width: labelWidth,
            }}>
              <Typography
                sx={{
                  fontSize: theme.typography.body1.fontSize,
                  fontWeight: 'medium',
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>

              {subTitle && <Typography
                sx={{
                  fontSize: theme.typography.body2.fontSize,
                  color: 'text.secondary',
                }}
              >
                {subTitle}
              </Typography>}
            </Box>

            {titleEndComponent}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {children}
        </AccordionDetails>
      </MuiAccordion>
    </Box>
  )
}